import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { WhatsAppButton } from "@/components/site/WhatsAppButton";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Blog from "@/pages/Blog";
import BlogDetail from "@/pages/BlogDetail";
import Contact from "@/pages/Contact";
import RequestQuote from "@/pages/RequestQuote";
import BookSiteVisit from "@/pages/BookSiteVisit";
import TrackProject from "@/pages/TrackProject";
import Login from "@/pages/Login";
import Careers from "@/pages/Careers";
import Gallery from "@/pages/Gallery";
import AuthCallback from "@/pages/AuthCallback";
import { AccessDenied } from "@/components/auth/AccessDenied";
import { FIELD_STAFF_JOB_ROLES } from "@/lib/roles";

const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminLeads = lazy(() => import("@/pages/admin/AdminLeads"));
const AdminSiteVisits = lazy(() => import("@/pages/admin/AdminSiteVisits"));
const AdminProjects = lazy(() => import("@/pages/admin/AdminProjects"));
const AdminTestimonials = lazy(() => import("@/pages/admin/AdminTestimonials"));
const AdminMessages = lazy(() => import("@/pages/admin/AdminMessages"));
const AdminVendors = lazy(() => import("@/pages/admin/AdminVendors"));
const AdminProcurement = lazy(() => import("@/pages/admin/AdminProcurement"));
const AdminBilling = lazy(() => import("@/pages/admin/AdminBilling"));
const AdminSitePhotos = lazy(() => import("@/pages/admin/AdminSitePhotos"));
const AdminReports = lazy(() => import("@/pages/admin/AdminReports"));
const AdminBackups = lazy(() => import("@/pages/admin/AdminBackups"));
const AdminCareers = lazy(() => import("@/pages/admin/AdminCareers"));
const AdminEmployees = lazy(() => import("@/pages/admin/AdminEmployees"));
const AdminAttendance = lazy(() => import("@/pages/admin/AdminAttendance"));
const AdminPayroll = lazy(() => import("@/pages/admin/AdminPayroll"));
const AdminPerformance = lazy(() => import("@/pages/admin/AdminPerformance"));
const AdminHR = lazy(() => import("@/pages/admin/AdminHR"));
const AdminHrReports = lazy(() => import("@/pages/admin/AdminHrReports"));
const AdminFieldOps = lazy(() => import("@/pages/admin/AdminFieldOps"));
const AdminLabour = lazy(() => import("@/pages/admin/AdminLabour"));
const AdminFinance = lazy(() => import("@/pages/admin/AdminFinance"));
const AdminOwnerFinance = lazy(() => import("@/pages/admin/AdminOwnerFinance"));
const AdminAllocation = lazy(() => import("@/pages/admin/AdminAllocation"));
const AdminInventory = lazy(() => import("@/pages/admin/AdminInventory"));
const AdminApprovals = lazy(() => import("@/pages/admin/AdminApprovals"));
const AdminEmployeeProfile = lazy(() => import("@/pages/admin/AdminEmployeeProfile"));
const AdminProjectControl = lazy(() => import("@/pages/admin/AdminProjectControl"));
const AdminEstimates = lazy(() => import("@/pages/admin/AdminEstimates"));
const AdminEstimateEditor = lazy(() => import("@/pages/admin/AdminEstimateEditor"));
const AdminMsrLibrary = lazy(() => import("@/pages/admin/AdminMsrLibrary"));
const AccountsPortal = lazy(() => import("@/pages/accounts/AccountsPortal"));
const EmployeePortal = lazy(() => import("@/pages/employee/EmployeePortal"));
const VendorPortal = lazy(() => import("@/pages/vendor/VendorPortal"));
const Portal = lazy(() => import("@/pages/portal/Portal"));
const PortalProject = lazy(() => import("@/pages/portal/PortalProject"));

const RouteLoader = () => (
  <div className="min-h-[50vh] flex items-center justify-center text-silver uppercase tracking-widest text-sm">Loading…</div>
);

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const PublicLayout = () => (
  <>
    <Navbar />
    <main className="pt-[72px]"><Outlet /></main>
    <Footer />
    <WhatsAppButton />
  </>
);

const Authenticating = () => (
  <div className="min-h-screen flex items-center justify-center text-silver uppercase tracking-widest text-sm">Authenticating…</div>
);

const AdminGuard = () => {
  const { user } = useAuth();
  if (user === undefined) return <Authenticating />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <AccessDenied />;
  return <AdminLayout />;
};

const PortalGuard = () => {
  const { user } = useAuth();
  if (user === undefined) return <Authenticating />;
  if (!user) return <Navigate to="/login" replace />;
  return <Outlet />;
};

const RoleGuard = ({ allow }) => {
  const { user } = useAuth();
  if (user === undefined) return <Authenticating />;
  if (!user) return <Navigate to="/login" replace />;
  if (!allow(user)) return <AccessDenied />;
  return <Outlet />;
};

const isEmployee = (u) => u.role === "employee" || u.role === "supervisor" || u.role === "admin";
const SPECIAL_JOB_ROLES = ["store_keeper", "procurement_manager", "driver", ...FIELD_STAFF_JOB_ROLES];
const isEngineer = (u) => u.role === "admin" || ((u.role === "employee" || u.role === "supervisor") && !SPECIAL_JOB_ROLES.includes(u.job_role));
const isInventory = (u) => u.role === "admin" || u.job_role === "store_keeper";
const isProcurement = (u) => u.role === "admin" || u.job_role === "procurement_manager";
const isDriver = (u) => u.role === "admin" || u.job_role === "driver";
const isFieldStaff = (u) => u.role === "admin" || FIELD_STAFF_JOB_ROLES.includes(u.job_role);
const isAccounts = (u) => u.role === "accountant" || u.role === "admin";
const isVendor = (u) => u.role === "vendor";

function AppRoutes() {
  const location = useLocation();
  // Google OAuth returns with #session_id=... — must be handled before any route/guard runs
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<RouteLoader />}>
      <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/:slug" element={<ServiceDetail />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/projects/:slug" element={<ProjectDetail />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogDetail />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/request-quote" element={<RequestQuote />} />
              <Route path="/book-site-visit" element={<BookSiteVisit />} />
              <Route path="/track" element={<TrackProject />} />
              <Route path="/login" element={<Login />} />
              <Route path="/portal" element={<PortalGuard />}>
                <Route index element={<Portal />} />
                <Route path=":projectId" element={<PortalProject />} />
              </Route>
              <Route path="/employee" element={<RoleGuard allow={isEmployee} />}>
                <Route index element={<EmployeePortal />} />
              </Route>
              <Route path="/engineer/dashboard" element={<RoleGuard allow={isEngineer} />}>
                <Route index element={<EmployeePortal />} />
              </Route>
              <Route path="/inventory/dashboard" element={<RoleGuard allow={isInventory} />}>
                <Route index element={<EmployeePortal />} />
              </Route>
              <Route path="/procurement/dashboard" element={<RoleGuard allow={isProcurement} />}>
                <Route index element={<EmployeePortal />} />
              </Route>
              <Route path="/driver/dashboard" element={<RoleGuard allow={isDriver} />}>
                <Route index element={<EmployeePortal />} />
              </Route>
              <Route path="/site/dashboard" element={<RoleGuard allow={isFieldStaff} />}>
                <Route index element={<EmployeePortal />} />
              </Route>
              <Route path="/vendor" element={<RoleGuard allow={isVendor} />}>
                <Route index element={<VendorPortal />} />
              </Route>
              <Route path="/accounts" element={<RoleGuard allow={isAccounts} />}>
                <Route index element={<AccountsPortal />} />
              </Route>
              <Route path="/finance/dashboard" element={<RoleGuard allow={isAccounts} />}>
                <Route index element={<AccountsPortal />} />
              </Route>
            </Route>
            <Route path="/admin" element={<AdminGuard />}>
              <Route index element={<AdminDashboard />} />
              <Route path="dashboard" element={<Navigate to="/admin" replace />} />
              <Route path="leads" element={<AdminLeads />} />
              <Route path="site-visits" element={<AdminSiteVisits />} />
              <Route path="projects" element={<AdminProjects />} />
              <Route path="projects/:projectId/control" element={<AdminProjectControl />} />
              <Route path="procurement" element={<AdminProcurement />} />
              <Route path="materials" element={<Navigate to="/admin/procurement" replace />} />
              <Route path="vendors" element={<AdminVendors />} />
              <Route path="billing" element={<AdminBilling />} />
              <Route path="collections" element={<Navigate to="/admin/billing" replace />} />
              <Route path="invoices" element={<Navigate to="/admin/billing" replace />} />
              <Route path="site-photos" element={<AdminSitePhotos />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="backups" element={<AdminBackups />} />
              <Route path="employees" element={<AdminEmployees />} />
              <Route path="attendance" element={<AdminAttendance />} />
              <Route path="payroll" element={<AdminPayroll />} />
              <Route path="performance" element={<AdminPerformance />} />
              <Route path="hr" element={<AdminHR />} />
              <Route path="hr-reports" element={<AdminHrReports />} />
              <Route path="employees/:employeeId" element={<AdminEmployeeProfile />} />
              <Route path="field-ops" element={<AdminFieldOps />} />
              <Route path="labour" element={<AdminLabour />} />
              <Route path="finance" element={<AdminFinance />} />
              <Route path="owner-finance" element={<AdminOwnerFinance />} />
              <Route path="allocation" element={<AdminAllocation />} />
              <Route path="estimates" element={<AdminEstimates />} />
              <Route path="estimates/:estimateId" element={<AdminEstimateEditor />} />
              <Route path="msr" element={<AdminMsrLibrary />} />
              <Route path="receivables" element={<Navigate to="/admin/finance?tab=receivables" replace />} />
              <Route path="payables" element={<Navigate to="/admin/finance?tab=payables" replace />} />
              <Route path="documents" element={<Navigate to="/admin/employees" replace />} />
              <Route path="inventory" element={<AdminInventory />} />
              <Route path="site-ops" element={<Navigate to="/admin/inventory" replace />} />
              <Route path="approvals" element={<AdminApprovals />} />
              <Route path="careers" element={<AdminCareers />} />
              <Route path="testimonials" element={<AdminTestimonials />} />
              <Route path="messages" element={<AdminMessages />} />
              <Route path="*" element={<Navigate to="/admin" replace />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="bottom-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
